import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import clsx from "clsx";
import { ArrowRight } from "lucide-react";
import { Section, Container, Eyebrow, RevealHeading, Reveal } from "@/components/primitives";
import { Seo } from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT_AS, HELP_WITH } from "@/data/site";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={clsx(
      "rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200",
      active
        ? "border-govx-green bg-govx-green text-white"
        : "border-govx-charcoal/20 text-govx-charcoal/75 hover:border-govx-green hover:text-govx-green"
    )}
  >
    {children}
  </button>
);

const Field = ({ label, name, value, onChange, type = "text", required, placeholder }) => (
  <label className="block">
    <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-govx-grey">{label}</span>
    <input
      data-testid={`contact-${name}`}
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(e) => onChange(name, e.target.value)}
      className="mt-2 w-full border-b border-govx-charcoal/25 bg-transparent py-2.5 text-base text-govx-charcoal outline-none transition-colors focus:border-govx-green"
    />
  </label>
);

export default function Contact() {
  const [form, setForm] = useState({
    contact_as: [],
    need_help_with: [],
    name: "",
    organisation: "",
    designation: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) =>
    setForm((f) => {
      const arr = f[k];
      return { ...f, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short description of the challenge.");
      return;
    }
    if (form.contact_as.length === 0 || form.need_help_with.length === 0) {
      toast.error("Please select at least one option in each of the two lists above.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Thank you — your enquiry has reached GovX. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact | GovX Nigeria"
        description="Tell GovX Nigeria what you're trying to build, fix or deliver. Start a conversation about strategy, delivery, technology or measurement."
      />
      <section className="relative bg-govx-black pt-[74px] text-govx-paper grain">
        <Container className="relative z-10 py-20 lg:py-24">
          <Eyebrow dark>Contact</Eyebrow>
          <RevealHeading
            lines={["Let's Solve", "A Real Problem."]}
            className="mt-7 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-6xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-govx-grey sm:text-lg">
              Tell us what you're trying to build, fix or deliver.
            </p>
          </Reveal>
        </Container>
      </section>

      <Section className="py-20 lg:py-28">
        <Container>
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-2xl border border-govx-green/40 bg-white p-10 text-center"
              data-testid="contact-success"
            >
              <div className="font-mono text-xs text-govx-green">ENQUIRY RECEIVED</div>
              <h2 className="mt-5 font-display text-3xl font-medium tracking-tight">Thank you.</h2>
              <p className="mt-4 text-govx-charcoal/70">
                Your message has reached GovX Nigeria. Our team will review the challenge and reach
                out to you shortly.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]" data-testid="contact-form">
              <div>
                <p className="max-w-sm text-base leading-relaxed text-govx-charcoal/70">
                  Whether it's a stalled programme, a political organisation to build, a platform to
                  ship or an outcome to measure — start here.
                </p>
                <div className="mt-10 space-y-2 border-t border-govx-charcoal/10 pt-6 font-mono text-sm text-govx-charcoal/70">
                  <div>contact@govxnigeria.com</div>
                  <div>Lagos, Nigeria</div>
                </div>
              </div>

              <div className="space-y-10">
                <div>
                  <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-govx-grey">
                    I am contacting GovX as <span className="text-govx-green">· select all that apply</span>
                  </span>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    {CONTACT_AS.map((o) => (
                      <Chip
                        key={o}
                        active={form.contact_as.includes(o)}
                        onClick={() => toggle("contact_as", o)}
                        testid={`contact-as-${o.split(" ")[0].toLowerCase()}`}
                      >
                        {o}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-govx-grey">
                    What do you need help with <span className="text-govx-green">· select all that apply</span>
                  </span>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    {HELP_WITH.map((o) => (
                      <Chip
                        key={o}
                        active={form.need_help_with.includes(o)}
                        onClick={() => toggle("need_help_with", o)}
                        testid={`help-with-${o.split(" ")[0].toLowerCase()}`}
                      >
                        {o}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Name *" name="name" value={form.name} onChange={set} required />
                  <Field label="Organisation" name="organisation" value={form.organisation} onChange={set} />
                  <Field label="Designation" name="designation" value={form.designation} onChange={set} />
                  <Field label="Phone" name="phone" value={form.phone} onChange={set} />
                  <Field label="Email *" name="email" type="email" value={form.email} onChange={set} required />
                </div>

                <label className="block">
                  <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-govx-grey">
                    Tell us about the challenge *
                  </span>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    rows={4}
                    required
                    className="mt-2 w-full resize-none border-b border-govx-charcoal/25 bg-transparent py-2.5 text-base text-govx-charcoal outline-none transition-colors focus:border-govx-green"
                  />
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-2 rounded-full bg-govx-green px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-govx-green-deep disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Start the Conversation"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          )}
        </Container>
      </Section>
    </>
  );
}
